import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Chip } from '@/components/Chip/Chip';
import { DeleteButton } from '@/components/DeleteButton/DeleteButton';
import { DiagnosticList } from '@/components/DiagnosticList/DiagnosticList';
import { Select } from '@/components/Select/Select';
import { TextField } from '@/components/TextField/TextField';
import type {
  Diagnostic,
  MessageTemplateScope,
  MessageTemplateStatus,
  MessageTemplateWithHistory,
  TemplateVersion,
} from '@/graphql/message-templates';
import { formatDate, shortId } from '@/lib/format';
import { SCOPE_LABELS } from '@/lib/scopes';

import styles from './TemplateModal.module.css';

export type TemplateAction = 'duplicate' | 'revert' | 'categorize' | 'status' | 'delete';

export interface TemplateModalProps {
  template: MessageTemplateWithHistory | null;
  loading?: boolean;
  /** A refusal about the read. */
  error?: string | null;
  /** The last write's envelope errors — about a stored body the engine now
   *  refuses, which is what a revert can turn up. */
  writeErrors?: readonly Diagnostic[];
  /** The last write's transport refusal — a scope the token cannot reach, a
   *  version number the template never had. */
  writeError?: string | null;
  busyAction?: TemplateAction | null;
  /** Which version's revert is in flight. One row is being written; spinning
   *  every row's button would say four are. */
  revertingVersion?: number | null;
  /** Where a duplicate may be sent. A brokerage destination needs an org. */
  duplicateScopes: readonly MessageTemplateScope[];
  onClose: () => void;
  onEdit: () => void;
  onDuplicate: (destination: MessageTemplateScope) => void;
  onRevert: (versionNumber: number) => void;
  onCategorize: (category: string | null) => void;
  /** Offer the template in the composer, or take it back out. Mints no
   *  version: what a template says and whether it may be sent are different
   *  facts. */
  onSetStatus: (status: MessageTemplateStatus) => void;
  onDelete: () => void;
  /** The editor, when one is open, and the render panel. The container owns
   *  both because both are driven by requests in flight. */
  children?: ReactNode;
}

/**
 * One template, opened: what it is, everything it has said, and the two things
 * you came to do to it — change it, and render it.
 *
 * A modal rather than a second column, for the reason the files harness opens
 * one: the listing is the page, and a detail pane beside it makes both halves
 * narrow enough that neither reads. Here the cost was concrete — a history row
 * squeezed to one character of title.
 *
 * The history is why this is more than an editor. Versions are immutable and
 * never soft-deleted, so the list is an append-only account of what the
 * template said and when — which is why **revert mints rather than repoints**:
 * putting the pointer back on version 3 would erase the revert from the very
 * record it is being read out of.
 */
export function TemplateModal({
  template,
  loading = false,
  error = null,
  writeErrors = [],
  writeError = null,
  busyAction = null,
  revertingVersion = null,
  duplicateScopes,
  onClose,
  onEdit,
  onDuplicate,
  onRevert,
  onCategorize,
  onSetStatus,
  onDelete,
  children,
}: TemplateModalProps) {
  const [destination, setDestination] = useState<MessageTemplateScope>(
    duplicateScopes[0] ?? 'PERSON',
  );
  const [category, setCategory] = useState<string | null>(null);
  const [status, setStatus] = useState<MessageTemplateStatus | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const deleted = template?.deletedAt != null;
  const categoryValue = category ?? template?.category ?? '';
  // Keystone's own default, for the one row that predates the column and
  // answers null: `_status_filter` reads a NULL as ACTIVE rather than letting
  // a template vanish from its adviser's list with nothing on the row to
  // explain it, and the control has to say the same thing.
  const statusValue: MessageTemplateStatus = status ?? template?.status ?? 'ACTIVE';
  const title = template?.currentVersion?.title ?? (loading ? 'Loading…' : 'Template');

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <section
        aria-label={`Template: ${title}`}
        aria-modal="true"
        className={styles.dialog}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <header className={styles.head}>
          <div className={styles.heading}>
            <h2 className={styles.title}>{title}</h2>
            {template ? (
              <div className={styles.chips}>
                <Chip tone={template.tenantId ? 'good' : 'neutral'}>
                  {template.tenantId ? 'Brokerage' : 'Personal'}
                </Chip>
                <Chip tone={template.status === 'DRAFT' ? 'warn' : 'neutral'}>
                  {template.status === 'DRAFT' ? 'draft' : 'active'}
                </Chip>
                <Chip>{template.category ?? 'ungrouped'}</Chip>
                <Chip label="type">{template.type ?? '—'}</Chip>
                <Chip label="versions">{template.versionCount}</Chip>
                {deleted ? <Chip tone="bad">deleted</Chip> : null}
              </div>
            ) : null}
          </div>
          <div className={styles.headActions}>
            {template && !deleted ? (
              <DeleteButton
                busy={busyAction === 'delete'}
                label={template.currentVersion?.title ?? 'this template'}
                onDelete={onDelete}
              />
            ) : null}
            <button className={styles.close} onClick={onClose} type="button">
              Close
            </button>
          </div>
        </header>

        {error ? (
          <div className={styles.message}>
            <Callout tone="danger" title="Keystone refused this template">
              {error}
            </Callout>
          </div>
        ) : null}

        {!error && !template ? (
          <p className={styles.message} role="status">
            {loading ? 'Asking Keystone…' : 'No template.'}
          </p>
        ) : null}

        {template ? (
          <div className={styles.body}>
            <div className={styles.pane}>
              {deleted ? (
                <Callout tone="danger" title="Soft-deleted">
                  The template is gone from every list and its history is unreachable through it —
                  the version rows themselves are retained. It stays on screen until this page is
                  reloaded.
                </Callout>
              ) : null}

              <dl className={styles.facts}>
                <Fact label="Template">{shortId(template.id)}</Fact>
                <Fact label="Configuration">{shortId(template.personConfigurationId)}</Fact>
                <Fact label="Tenant">{shortId(template.tenantId)}</Fact>
                <Fact label="Forked from">{shortId(template.duplicatedFromMessageTemplateId)}</Fact>
                <Fact label="Position">{template.position ?? '—'}</Fact>
                <Fact label="Created">{formatDate(template.createdAt)}</Fact>
                <Fact label="Updated">{formatDate(template.updatedAt)}</Fact>
              </dl>

              <pre aria-label="Current body" className={styles.bodyText}>
                {template.currentVersion?.body ?? ''}
              </pre>

              {writeError ? (
                <Callout tone="danger" title="Keystone refused the request">
                  {writeError}
                </Callout>
              ) : null}
              <DiagnosticList errors={writeErrors} />

              <div className={styles.actions}>
                <Button disabled={deleted} onClick={onEdit} type="button">
                  Edit
                </Button>

                <div className={styles.action}>
                  <Select
                    disabled={deleted || duplicateScopes.length === 0}
                    label="Duplicate to"
                    onChange={(value) => setDestination(value as MessageTemplateScope)}
                    options={duplicateScopes.map((value) => [value, SCOPE_LABELS[value]] as const)}
                    value={destination}
                  />
                  <Button
                    disabled={deleted || duplicateScopes.length === 0}
                    loading={busyAction === 'duplicate'}
                    onClick={() => onDuplicate(destination)}
                    type="button"
                    variant="ghost"
                  >
                    Duplicate
                  </Button>
                </div>

                <div className={styles.action}>
                  <TextField
                    disabled={deleted}
                    label="Category"
                    onChange={(event) => setCategory(event.target.value)}
                    placeholder="ungrouped"
                    value={categoryValue}
                  />
                  <Button
                    disabled={deleted}
                    loading={busyAction === 'categorize'}
                    onClick={() => onCategorize(categoryValue.trim() === '' ? null : categoryValue)}
                    type="button"
                    variant="ghost"
                  >
                    File
                  </Button>
                </div>

                <div className={styles.action}>
                  <Select
                    disabled={deleted}
                    label="Status"
                    onChange={(value) => setStatus(value as MessageTemplateStatus)}
                    options={[
                      ['ACTIVE', 'Active — offered in the composer'],
                      ['DRAFT', 'Draft — not offered'],
                    ]}
                    value={statusValue}
                  />
                  <Button
                    disabled={deleted}
                    loading={busyAction === 'status'}
                    onClick={() => onSetStatus(statusValue)}
                    type="button"
                    variant="ghost"
                  >
                    Set status
                  </Button>
                </div>
              </div>

              <h3 className={styles.historyHeading}>
                History
                <span className={styles.count}>{template.versionCount} versions</span>
              </h3>
              <ul aria-label="Version history" className={styles.history}>
                {template.versions.map((version, index) => (
                  <li className={styles.version} key={version.id}>
                    <div className={styles.versionHead}>
                      <span className={styles.versionNumber}>
                        v{version.versionNumber}
                        {version.id === template.currentVersionId ? (
                          <Chip tone="good">current</Chip>
                        ) : null}
                      </span>
                      <Button
                        // The label stays short so it cannot crowd the title
                        // beside it; the accessible name still says which
                        // version, because four buttons reading "Revert" are
                        // four identical buttons to anyone not looking at the
                        // row.
                        aria-label={`Revert to v${version.versionNumber}`}
                        disabled={deleted}
                        loading={
                          busyAction === 'revert' && revertingVersion === version.versionNumber
                        }
                        onClick={() => onRevert(version.versionNumber ?? 0)}
                        type="button"
                        variant="ghost"
                      >
                        Revert
                      </Button>
                    </div>
                    <Retitle previous={template.versions[index + 1]} version={version} />
                    <p className={styles.versionWho}>
                      {authorName(version)} · {formatDate(version.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.pane}>{children}</div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

/**
 * A version's title, where it is news.
 *
 * Printing it on every row put the same words down the whole pane — the header
 * says the current title, and a template rewritten three times without being
 * renamed then repeated it three times more. A title is a version's content,
 * so it belongs in the history, but what the history is for is *what changed*:
 * it shows here when this version renamed the template, and on the oldest row,
 * which is where the name came from and has nothing behind it to differ from.
 *
 * `previous` is the next row down — the list is newest first — so it is
 * `undefined` exactly on that oldest row.
 */
function Retitle({
  version,
  previous,
}: {
  version: TemplateVersion;
  previous: TemplateVersion | undefined;
}) {
  if (!previous) {
    return <p className={styles.versionTitle}>{version.title}</p>;
  }
  if (previous.title === version.title) {
    return null;
  }
  return (
    <p className={styles.versionTitle}>
      <span className={styles.retitled}>Retitled</span> {version.title}
    </p>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{children}</dd>
    </div>
  );
}

/**
 * A version's author, or that there is not one. Keystone answers null both for
 * a version minted out of band and for an author since erased, and a name is
 * ordinary to be missing anyway: a Person is created on first token contact
 * and carries no claims until something asserts one.
 */
function authorName(version: TemplateVersion): string {
  if (!version.createdBy) {
    return 'no author';
  }
  const name = [version.createdBy.firstName, version.createdBy.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return name || shortId(version.createdBy.id);
}
